import { useState, useRef, useCallback } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

function AboutPageSchema() {
  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '17c·moc起草糖心在线看',
    url: 'https://17c-moc.com',
    description: '17c·moc起草糖心在线看提供海量高清视频资源，无需下载，随时随地畅享在线观看体验。糖心在线看免费入口，最新内容持续更新，流畅播放，多终端兼容。',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@17c-moc.com',
      availableLanguage: 'Chinese',
    },
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 17c·moc起草糖心在线看',
    description: '了解17c·moc起草糖心在线看的品牌故事、平台优势与联系方式，糖心在线看免费入口。',
    url: 'https://17c-moc.com/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '17c·moc起草糖心在线看',
      url: 'https://17c-moc.com',
    },
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }} />
    </>
  );
}

const brandValues = [
  {
    icon: 'ri-shield-star-line',
    title: '高清画质',
    desc: '支持最高4K超高清画质，先进的视频编码技术，为您带来影院级的视觉享受。',
  },
  {
    icon: 'ri-flashlight-line',
    title: '流畅体验',
    desc: '全球CDN加速网络，智能自适应码率，无论身处何地都能享受流畅无卡顿的播放体验。',
  },
  {
    icon: 'ri-device-line',
    title: '多端兼容',
    desc: '完美适配PC、手机、平板和智能电视，一个账号全平台同步，随时随地想看就看。',
  },
  {
    icon: 'ri-shield-check-line',
    title: '安全可靠',
    desc: '严格的内容审核机制，采用最新的安全加密技术，保护您的隐私和观看体验。',
  },
];

const milestones = [
  { year: '2023', title: '平台创立', desc: '17c·moc起草糖心在线看正式上线，初始视频库5000+部。' },
  { year: '2024', title: '高速增长', desc: '月活用户突破500万，视频资源扩充至20000+部，上线4K画质支持。' },
  { year: '2025', title: '行业领先', desc: '月活用户突破1000万，CDN节点覆盖全球30+国家，成为行业领先的免费视频平台。' },
  { year: '2026', title: '持续创新', desc: '全面升级播放技术和推荐算法，推出个性化首页，进一步提升用户体验。' },
];

export default function AboutPage() {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const FORM_SUBMIT_ADDR = 'https://readdy.ai/api/form/d9phioub4qjhggaepui0';

  const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setFormSubmitted(true);
      return;
    }

    setFormError('');
    setIsSubmitting(true);

    try {
      const formData = new FormData(form);
      formData.delete('phone_alt');

      const response = await fetch(FORM_SUBMIT_ADDR, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: any = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse error
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setFormSubmitted(true);
        form.reset();
      } else {
        if (typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam')) {
          setFormError('提交失败，请稍后再试');
        } else {
          setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后再试');
        }
      }
    } catch {
      setFormError('网络错误，请稍后再试');
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  return (
    <>
      <AboutPageSchema />
      <Navbar />

      <main className="pt-20">
        <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />

        {/* Hero */}
        <section className="relative h-[340px] md:h-[440px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20warm%20atmospheric%20composition%20with%20gradient%20shapes%20in%20coral%20and%20charcoal%20tones%2C%20soft%20diffused%20light%2C%20modern%20minimalist%20digital%20art%2C%20corporate%20about%20us%20background%2C%20clean%20aesthetic%2C%20no%20text&width=1800&height=700&seq=about-hero-2026&orientation=landscape"
            alt="关于17c·moc起草糖心在线看"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/50"></div>
          <div className="relative z-10 container-custom text-center">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">关于我们</h1>
            <p className="text-white/70 text-base md:text-lg max-w-xl mx-auto">
              了解糖心在线看的品牌故事与发展历程
            </p>
          </div>
        </section>

        {/* Brand */}
        <section className="py-14">
          <div className="container-custom max-w-3xl">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                品牌介绍
              </h2>
              <p className="text-foreground-600 leading-relaxed text-base">
                <strong>17c·moc起草糖心在线看</strong>成立于2023年，是一家专注于<strong>免费高清视频在线播放</strong>的互联网平台。我们致力于为用户提供海量优质的视频资源和极致的在线观看体验。平台涵盖电影、电视剧、综艺、动漫、纪录片等多个分类，内容持续更新，画质最高可达4K超高清。无论您身在何处，只需一个浏览器，即可通过糖心在线看免费入口进入精彩纷呈的视频世界。
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {brandValues.map((item, i) => (
                <div key={i} className="bg-white rounded-xl p-5 border border-background-200/70">
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center mb-3">
                    <i className={`${item.icon} text-primary-600 text-lg`} style={{ width: '22px', height: '22px' }}></i>
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-900 mb-1.5">{item.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Timeline */}
        <section className="py-14 bg-background-100">
          <div className="container-custom max-w-3xl">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                发展历程
              </h2>
              <p className="text-foreground-500 text-sm">从创立到领先，每一步都有您的陪伴</p>
            </div>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-300 -translate-x-1/2"></div>
              <div className="space-y-8">
                {milestones.map((item, i) => (
                  <div
                    key={i}
                    className={`relative flex flex-col md:flex-row gap-4 md:gap-8 ${
                      i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className="flex items-center gap-4 md:w-1/2 md:justify-end">
                      <div
                        className={`bg-white rounded-xl p-4 border border-background-200/70 flex-1 ${
                          i % 2 === 1 ? 'md:text-right' : ''
                        }`}
                      >
                        <span className="inline-block bg-accent-100 text-accent-700 text-xs px-2 py-0.5 rounded-full mb-2">
                          {item.year}
                        </span>
                        <h3 className="font-heading font-semibold text-foreground-900 mb-1">{item.title}</h3>
                        <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                    <div className="hidden md:flex items-center justify-center w-8 flex-shrink-0">
                      <div className="w-3 h-3 bg-primary-500 rounded-full border-2 border-white"></div>
                    </div>
                    <div className="hidden md:block md:w-1/2"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-14">
          <div className="container-custom max-w-3xl">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                联系我们
              </h2>
              <p className="text-foreground-500 text-sm">有任何问题或建议？欢迎随时与我们联系</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
              <div className="bg-white rounded-xl p-5 border border-background-200/70 text-center">
                <div className="w-10 h-10 mx-auto mb-3 bg-accent-100 rounded-lg flex items-center justify-center">
                  <i className="ri-mail-line text-accent-600 text-lg" style={{ width: '22px', height: '22px' }}></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-900 mb-1">电子邮箱</h3>
                <p className="text-sm text-foreground-500">contact@17c-moc.com</p>
              </div>
              <div className="bg-white rounded-xl p-5 border border-background-200/70 text-center">
                <div className="w-10 h-10 mx-auto mb-3 bg-primary-100 rounded-lg flex items-center justify-center">
                  <i className="ri-customer-service-line text-primary-600 text-lg" style={{ width: '22px', height: '22px' }}></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-900 mb-1">在线客服</h3>
                <p className="text-sm text-foreground-500">工作日 9:00 - 21:00</p>
              </div>
              <div className="bg-white rounded-xl p-5 border border-background-200/70 text-center">
                <div className="w-10 h-10 mx-auto mb-3 bg-secondary-100 rounded-lg flex items-center justify-center">
                  <i className="ri-question-answer-line text-secondary-600 text-lg" style={{ width: '22px', height: '22px' }}></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-900 mb-1">反馈建议</h3>
                <p className="text-sm text-foreground-500">您的声音是我们进步的动力</p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 md:p-8 border border-background-200/70">
              {formSubmitted ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 mx-auto mb-4 bg-primary-100 rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-primary-500 text-2xl" style={{ width: '28px', height: '28px' }}></i>
                  </div>
                  <h3 className="font-heading text-xl font-bold text-foreground-900 mb-2">感谢您的留言！</h3>
                  <p className="text-foreground-500 text-sm">我们已收到您的信息，将在24小时内回复您。</p>
                </div>
              ) : (
                <form ref={formRef} onSubmit={handleSubmit} data-readdy-form="">
                  <h3 className="font-heading font-semibold text-foreground-900 text-lg mb-5">发送消息</h3>
                  {formError && (
                    <div className="mb-4 p-3 bg-primary-50 text-primary-700 rounded-lg text-sm border border-primary-200">
                      {formError}
                    </div>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="name" className="block text-sm text-foreground-700 mb-1.5 font-medium">
                        姓名 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm text-foreground-700 mb-1.5 font-medium">
                        邮箱 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>
                  <div className="mb-4">
                    <label htmlFor="subject" className="block text-sm text-foreground-700 mb-1.5 font-medium">
                      主题
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入消息主题"
                    />
                  </div>
                  <div className="mb-1">
                    <label htmlFor="message" className="block text-sm text-foreground-700 mb-1.5 font-medium">
                      留言内容 <span className="text-primary-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                      placeholder="请输入您的留言，最多500字"
                    />
                  </div>
                  <input
                    type="text"
                    name="phone_alt"
                    className="honeypot-field"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                  <div className="mt-4">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full py-3 bg-primary-500 text-white font-medium rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer text-sm"
                    >
                      {isSubmitting ? '发送中...' : '发送消息'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}